import { sendMessageAgentSession } from 'actions/agentSessions';
import LoadingIndicator from 'components/LoadingIndicator';
import keyMap from 'constants/configHotKeys';
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { GlobalHotKeys } from 'react-hotkeys';
import LoadingOverlayWrapper from 'react-loading-overlay-ts';
import { useDispatch } from 'react-redux';
import {
  getAgentSessionConnectionLoading,
  getConnectionStatus,
  getSessionMessages,
} from 'selectors/agentSessions';
import { useSelector } from 'store';
import AgentChatMessage from './AgentChatMessage';
import AgentChatMessageLoader from './AgentChatMessageLoader';

type FormValues = {
  message: string;
};

const AgentChat: React.FC = () => {
  const dispatch = useDispatch();

  const connecting = useSelector(getAgentSessionConnectionLoading);
  const messages = useSelector(getSessionMessages);
  const status = useSelector(getConnectionStatus);

  const chatRef = useRef<HTMLDivElement>(null);
  const sendButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current?.scrollBy({ top: chatRef.current.scrollHeight });
    }
  }, [messages.length, status]);

  const { handleSubmit, register, reset } = useForm<FormValues>({
    defaultValues: {
      message: '',
    },
  });

  const onSubmit = ({ message }: FormValues): void => {
    reset();
    dispatch(sendMessageAgentSession({ message }));
  };

  const handlers = {
    SEND_AGENT_CHAT_MESSAGE: (): void => {
      sendButtonRef?.current?.click();
    },
  };

  return (
    <>
      <GlobalHotKeys keyMap={keyMap} handlers={handlers} />
      <div className="w-full h-full flex flex-row gap-4 bg-white overflow-hidden">
        <div className="h-full w-1/2 flex flex-col gap-2">
          <textarea
            id="message"
            ref={register({ required: true, pattern: /\S/i })}
            name="message"
            rows={10}
            placeholder="Write the content of the communication."
            className="form-input w-full h-full p-2 text-body rounded resize-none placeholder-italic placeholder-litlingo-gray-6"
          />
          <div className="flex flex-row items-start justify-between">
            <div>
              <p className="text-small">
                <span className="text-litlingo-gray-4">
                  <strong>Ctrl + Enter </strong>
                  sends message.
                </span>
              </p>
            </div>
            <button
              ref={sendButtonRef}
              type="button"
              className="button button--primary w-40 h-8 justify-center self-end"
              onClick={handleSubmit(onSubmit)}
            >
              Send
            </button>
          </div>
        </div>

        <div className="h-full pb-10 w-1/2 flex overflow-hidden">
          <LoadingOverlayWrapper
            active={connecting}
            spinner={<LoadingIndicator size="10" />}
            fadeSpeed={0}
            styles={{
              content: (base) => ({
                ...base,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }),
              wrapper: (base) => ({
                ...base,
                height: '100%',
                width: '100%',
              }),
              spinner: () => ({}),
              overlay: (
                base: Record<string, unknown>
              ): { [key: string]: number; zIndex: number } => ({
                ...base,
                zIndex: 10,
              }),
            }}
          >
            <div
              ref={chatRef}
              className="h-full w-full pt-4 pb-10 flex flex-col gap-6 overflow-auto"
            >
              {messages.map((message, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <AgentChatMessage key={`${idx}-${message.content}`} message={message} />
              ))}
              {status === 'running' && <AgentChatMessageLoader key="running-placeholder" />}
            </div>
          </LoadingOverlayWrapper>
        </div>
      </div>
    </>
  );
};

export default AgentChat;
